import {LocalProvider} from "../providers/local-provider.js";
import {
    DEFAULT_DURATION,
    DEFAULT_ERROR_DURATION, DEFAULT_STORE_NAME,
    DEFAULT_SUCCESS_DURATION,
    TOAST_TYPE_DANGER,
    TOAST_TYPE_INFO,
    TOAST_TYPE_SUCCESS,
    TOAST_TYPE_WARNING
} from "./toast-messages-config.js";

export class ToastMessagesProvider extends LocalProvider {
    constructor(props) {
        super({
            ...props,
            routeName: '...',
            name: DEFAULT_STORE_NAME,
        });
    }
}

let provider;
function getProvider() {
    if (!provider) {
        provider = new ToastMessagesProvider();
    }
    return provider;
}

/**
 * @typedef {object} ToastMessage
 * @property {number} id ID wiadomości
 * @property {string} message Treść wiadomości
 * @property {'INFO' | 'DANGER' | 'WARNING' | 'SUCCESS'} type Typ powiadomienia
 * @property {number | undefined} duration Czas trwania powiadomienia, Infinity – nie ukrywa automatycznie
 * @property {string | undefined} actionTitle Etykieta dodatkowego przycisku
 * @property {Function | undefined} actionHandler Akcja dodatkowego przycisku. Automatycznie zamyka powiadomienia.
 * W argumencie zostanie przekazany obiekt Event kliknięcia. Aby pominąć automatyczne zamknięcie można anulować na nim
 * domyślną akcję: `event.preventDefault()`
 * @property {string | undefined} icon Ikona powiadomienia ze zbioru zdefiniowanego w parametrze `collection`
 * @property {'solid' | 'regular' | 'light' | 'dual_tone' | undefined} iconType Typ ikona powiadomienia ze zbioru FontAwesome
 * @property {'fa' | 'bi' | 'pk' | undefined} iconCollection Zbiór ikon" fa - FontAwesome, bi - Bootstrap, pk - Polkurier. Domyślny: fa
 * @property {boolean | undefined} closable Czy wyświetlić przycisk zamykania. Domyślnie TRUE
 * @property {boolean | undefined} spinner Czy wyświetlić spinner zamiast ikony
 */

let nextId = 1;

/**
 * @param {string} message Wiadomość do wyświetlenia w powiadomieniu
 * @param {Partial<ToastMessage>} props Parametry do konfiguracji powiadomienia
 * @return {ToastMessage}
 */
export function createToast(message, props = {}) {
    const toast = {
        id: nextId++,
        message: message || '',
        type: props.type || TOAST_TYPE_INFO,
        duration: props.duration ?? DEFAULT_DURATION,
        actionTitle: props.actionTitle || '',
        actionHandler: props.actionHandler,
        icon: props.icon || '',
        iconType: props.iconType || '',
        iconCollection: props.iconCollection || '',
        closable: props.closable !== false,
        spinner: props.spinner === true,
    };
    getProvider().add([toast]);
    return toast;
}

/**
 * @param {string} message
 * @param {Partial<ToastMessage>} props
 * @return {ToastMessage}
 */
export function createInfoToast(message, props = {}) {
    return createToast(message, {
        duration: DEFAULT_DURATION,
        ...props,
        type: TOAST_TYPE_INFO,
    });
}

/**
 * @param {string} message
 * @param {Partial<ToastMessage>} props
 * @return {ToastMessage}
 */
export function createSuccessToast(message, props = {}) {
    return createToast(message, {
        duration: DEFAULT_SUCCESS_DURATION,
        ...props,
        type: TOAST_TYPE_SUCCESS,
    });
}

/**
 * @param {string} message
 * @param {Partial<ToastMessage>} props
 * @return {ToastMessage}
 */
export function createWarningToast(message, props = {}) {
    return createToast(message, {
        duration: DEFAULT_DURATION,
        ...props,
        type: TOAST_TYPE_WARNING,
    });
}

/**
 * @param {string} message
 * @param {Partial<ToastMessage>} props
 * @return {ToastMessage}
 */
export function createErrorToast(message, props = {}) {
    return createToast(message, {
        duration: DEFAULT_ERROR_DURATION,
        ...props,
        type: TOAST_TYPE_DANGER,
    });
}

/**
 * Usuwa wiadomość
 * @param {ToastMessage | number} messageOrId
 */
export function removeToastMessage(messageOrId) {
    const provider = getProvider();
    let message = messageOrId;
    if (typeof messageOrId === 'number') {
        message = provider.findByPk(messageOrId);
    }
    if (message) {
        provider.add([{
            ...message,
            isClosed: true,
            closedAt: Date.now(),
        }]);
    }
}

/**
 * Aktualizuje stan wiadomości
 * @param {number} messageId
 * @param {Partial<ToastMessage>} props
 * @return {ToastMessage | undefined}
 */
export function updateToastMessage(messageId, props) {
    const previousMessage = getProvider().findByPk(messageId);
    if (!previousMessage) {
        return;
    }

    const nextMessage = {
        ...previousMessage,
        ...props,
    }
    getProvider().add([nextMessage]);
    return nextMessage;
}
